import React, { useRef, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useMediaQuery } from '@material-ui/core';
import { createTheme, makeStyles, useTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import { useHistory } from 'react-router-dom';
import { useCheckAuth } from 'ra-core';

import defaultTheme from './defaultTheme';
import Notification from './notification';
import SignInForm from '../../components/ui/sign-in/sign-in-form';

const useStyles = makeStyles(
  (theme) => ({
    main: {
      display: 'flex',
      flexDirection: 'row',
      minHeight: '100vh',
      height: '100vh',
      width: '100vw',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    leftSection: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing(4),
    },
    formContainer: {
      width: '100%',
      maxWidth: '448px',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 600,
      lineHeight: 1.2,
      marginBottom: theme.spacing(1.5),
      color: theme.palette.text.primary,
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
      },
    },
    description: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(3),
      fontSize: '1rem',
    },
    rightSection: {
      flex: 1,
      position: 'relative',
      padding: theme.spacing(2),
      display: 'none',
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },
    heroImage: {
      position: 'absolute',
      inset: theme.spacing(1),
      borderRadius: '24px',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    testimonialsContainer: {
      position: 'absolute',
      bottom: theme.spacing(2),
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: theme.spacing(1),
      padding: `0 ${theme.spacing(2)}`,
      width: '100%',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    testimonialCard: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: theme.spacing(1.5),
      borderRadius: '24px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      padding: theme.spacing(2.5),
      width: '256px',
      animation: '$fadeIn 0.6s ease-out',
    },
    testimonialAvatar: {
      height: '40px',
      width: '40px',
      objectFit: 'cover',
      borderRadius: '16px',
    },
    testimonialContent: {
      fontSize: '14px',
      lineHeight: 1.4,
    },
    testimonialName: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      fontWeight: 500,
      marginBottom: theme.spacing(0.5),
    },
    testimonialHandle: {
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(0.5),
    },
    testimonialText: {
      color: theme.palette.text.primary,
      opacity: 0.8,
      marginTop: theme.spacing(0.5),
    },
    '@keyframes fadeIn': {
      from: {
        opacity: 0,
        transform: 'translateY(20px)',
      },
      to: {
        opacity: 1,
        transform: 'translateY(0)',
      },
    },
  }),
  { name: 'RaLogin' }
);

/**
 * A standalone login page, to serve as authentication gate to the admin
 *
 * Expects the user to enter a login and a password, which will be checked
 * by the `authProvider.login()` method. Redirects to the root page (/)
 * upon success, otherwise displays an authentication error message.
 *
 * Copy and adapt this component to implement your own login logic
 * (e.g. to authenticate via email or facebook or anything else).
 *
 * @example
 *     import MyLoginPage from './MyLoginPage';
 *     const App = () => (
 *         <Admin loginPage={MyLoginPage} authProvider={authProvider}>
 *             ...
 *        </Admin>
 *     );
 */
const TestimonialCard = ({ testimonial, delay }) => {
  const classes = useStyles();
  return (
    <div className={classnames(classes.testimonialCard)} style={{ animationDelay: delay }}>
      <img
        src={testimonial.avatarSrc}
        className={classes.testimonialAvatar}
        alt={testimonial.name}
      />
      <div className={classes.testimonialContent}>
        <p className={classes.testimonialName}>{testimonial.name}</p>
        <p className={classes.testimonialHandle}>{testimonial.handle}</p>
        <p className={classes.testimonialText}>{testimonial.text}</p>
      </div>
    </div>
  );
};

TestimonialCard.propTypes = {
  testimonial: PropTypes.shape({
    avatarSrc: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    handle: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
  }).isRequired,
  delay: PropTypes.string,
};

const LoginContent = ({
  title,
  description,
  className,
  children,
  heroImageSrc,
  testimonials = [],
  onGoogleSignIn,
  onResetPassword,
  onCreateAccount,
  classes,
  heroImageRef,
  ...rest
}) => {
  const materialTheme = useTheme();
  const isXl = useMediaQuery(materialTheme.breakpoints.up('xl'));
  const is2Xl = useMediaQuery('(min-width:1536px)');

  const defaultTitle = (
    <span style={{ fontWeight: 300, letterSpacing: '-0.02em' }}>
      Welcome to wiBBes
    </span>
  );

  const defaultDescription = 'Access your account and start uploading your contents';

  return (
    <div className={classnames(classes.main, className)} {...rest}>
      <section className={classes.leftSection}>
        <div className={classes.formContainer}>
          <h1 className={classes.title}>
            {title || defaultTitle}
          </h1>
          <p className={classes.description}>
            {description || defaultDescription}
          </p>
          {children || (
            <SignInForm
              onGoogleSignIn={onGoogleSignIn}
              onResetPassword={onResetPassword}
              onCreateAccount={onCreateAccount}
            />
          )}
        </div>
      </section>
      {heroImageSrc && (
        <section className={classes.rightSection}>
          <div className={classes.heroImage} ref={heroImageRef} />
          {testimonials.length > 0 && (
            <div className={classes.testimonialsContainer}>
              {testimonials[0] && (
                <TestimonialCard testimonial={testimonials[0]} delay="0.1s" />
              )}
              {testimonials[1] && isXl && (
                <TestimonialCard testimonial={testimonials[1]} delay="0.2s" />
              )}
              {testimonials[2] && is2Xl && (
                <TestimonialCard testimonial={testimonials[2]} delay="0.3s" />
              )}
            </div>
          )}
        </section>
      )}
      <Notification />
    </div>
  );
};

LoginContent.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
  heroImageSrc: PropTypes.string,
  testimonials: PropTypes.array,
  onGoogleSignIn: PropTypes.func,
  onResetPassword: PropTypes.func,
  onCreateAccount: PropTypes.func,
  classes: PropTypes.object.isRequired,
  heroImageRef: PropTypes.object.isRequired,
};

const Login = (props) => {
  const {
    theme,
    title,
    description,
    classes: classesOverride,
    className,
    children,
    staticContext,
    heroImageSrc,
    testimonials = [],
    onGoogleSignIn,
    onResetPassword,
    onCreateAccount,
    ...rest
  } = props;
  const heroImageRef = useRef();
  const classes = useStyles(props);
  const muiTheme = useMemo(() => createTheme(theme), [theme]);
  const checkAuth = useCheckAuth();
  const history = useHistory();

  useEffect(() => {
    checkAuth({}, false)
      .then(() => {
        // already authenticated, redirect to the home page
        history.push('/');
      })
      .catch(() => {
        // not authenticated, stay on the login page
      });
  }, [checkAuth, history]);

  // Load background image asynchronously
  useEffect(() => {
    if (heroImageSrc && heroImageRef.current) {
      const img = new Image();
      img.onload = () => {
        if (heroImageRef.current) {
          heroImageRef.current.style.backgroundImage = `url(${heroImageSrc})`;
        }
      };
      img.src = heroImageSrc;
    }
  }, [heroImageSrc]);

  return (
    <ThemeProvider theme={muiTheme}>
      <LoginContent
        title={title}
        description={description}
        className={className}
        children={children}
        heroImageSrc={heroImageSrc}
        testimonials={testimonials}
        onGoogleSignIn={onGoogleSignIn}
        onResetPassword={onResetPassword}
        onCreateAccount={onCreateAccount}
        classes={classes}
        heroImageRef={heroImageRef}
        {...rest}
      />
    </ThemeProvider>
  );
};

Login.propTypes = {
  heroImageSrc: PropTypes.string,
  title: PropTypes.node,
  description: PropTypes.node,
  testimonials: PropTypes.arrayOf(
    PropTypes.shape({
      avatarSrc: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      handle: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    })
  ),
  onGoogleSignIn: PropTypes.func,
  onResetPassword: PropTypes.func,
  onCreateAccount: PropTypes.func,
  children: PropTypes.node,
  classes: PropTypes.object,
  className: PropTypes.string,
  theme: PropTypes.object,
  staticContext: PropTypes.object,
};

Login.defaultProps = {
  theme: defaultTheme,
  testimonials: [],
};

export default Login;
